// LIVEMOA · 개인정보처리방침 페이지 (Production, TikTok Login Kit Review 대응).
// 실제 backend 코드(User/Seller 모델, OAuth 토큰 컬렉션, 쿠키/localStorage) Audit 결과 기반.
import React from 'react';
import { Link } from 'react-router-dom';
import { BusinessInfo } from './Terms';
import LivemoaLegalHeader from '@/components/LivemoaLegalHeader';

const H2 = ({ children, id }) => (
  <h2 id={id} className="mt-10 mb-3 text-xl font-bold text-black dark:text-white">
    {children}
  </h2>
);
const H3 = ({ children }) => (
  <h3 className="mt-6 mb-2 text-base font-semibold text-black dark:text-white">{children}</h3>
);
const P = ({ children }) => (
  <p className="my-2 text-sm leading-7 text-gray-700 dark:text-gray-300">{children}</p>
);
const UL = ({ children }) => (
  <ul className="my-2 ml-5 list-disc space-y-1 text-sm leading-7 text-gray-700 dark:text-gray-300">
    {children}
  </ul>
);
const Table = ({ children }) => (
  <div className="my-3 overflow-x-auto">
    <table className="w-full text-xs border-collapse">{children}</table>
  </div>
);

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white dark:bg-[#0A0A0A]" data-testid="privacy-page">
      {/* TikTok Review T-R1 · 페이지 상단 LIVEMOA app icon 표시 (reviewer 반려사항 대응) */}
      <LivemoaLegalHeader current="privacy" />
      <main>
        <article className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
          <header className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-black dark:text-white">
              LIVEMOA 개인정보처리방침
            </h1>
            <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              시행일: 2026년 2월 13일 · 운영자: 엠지컴퍼니 (LIVEMOA · 라이브모아)
            </p>
            <p className="mt-3 text-sm leading-7 text-gray-700 dark:text-gray-300">
              엠지컴퍼니(이하 "회사")는 「개인정보 보호법」 등 관련 법령을 준수하며,
              이용자의 개인정보를 소중히 다룹니다. 본 방침은 회사가 운영하는 서비스
              LIVEMOA (라이브모아, 이하 "서비스")에서 실제로 처리되는 개인정보만을 기재합니다.
            </p>
          </header>

          <H2 id="p1">제1조 (개인정보의 처리 목적)</H2>
          <UL>
            <li>회원가입 및 로그인, 회원 식별·본인확인</li>
            <li>판매자 계정 심사·관리, 판매자 프로필 제공</li>
            <li>외부 플랫폼(TikTok Login Kit·YouTube OAuth 등) 계정 연결 및 라이브 방송 연동</li>
            <li>서비스 제공(라이브 편성표·팔로우·알림·상품·주문 편의 기능)</li>
            <li>고객문의 응대 및 서비스 개선</li>
            <li>부정 이용 방지, 서비스 보안 및 접근제어</li>
            <li>관련 법령에 따른 의무 이행</li>
          </UL>

          <H2 id="p2">제2조 (처리하는 개인정보 항목)</H2>
          <H3>1) 일반회원 · 판매자 공통</H3>
          <UL>
            <li>필수: 이름, 소셜 로그인 식별자(예: 카카오 회원번호)</li>
            <li>선택: 이메일, 전화번호, 프로필 이미지, 카카오 닉네임</li>
            <li>가입/연결 시 소셜 로그인 제공자(카카오 등)로부터 위 항목을 이용자 동의 하에 수신</li>
          </UL>
          <H3>2) 판매자 추가</H3>
          <UL>
            <li>판매자 표시명, 소개(bio), 프로필 사진</li>
            <li>외부 플랫폼 연결정보: Instagram·TikTok·YouTube·Naver 등 채널 식별자 및 공개 URL(이용자가 직접 입력)</li>
            <li>판매 카테고리, 팔로워 수(내부 집계값)</li>
          </UL>
          <H3>3) 외부 플랫폼 계정 연결 시(OAuth)</H3>
          <UL>
            <li><b>YouTube</b>: OAuth Access Token / Refresh Token(암호화 저장), 채널 ID, 채널명, 자동감지 설정값</li>
            <li><b>TikTok (Login Kit)</b>: OAuth Access Token / Refresh Token(암호화 저장), open_id, 표시명, 프로필 이미지, TikTok 사용자 요청 스코프(<code>user.info.basic</code>, <code>video.list</code>)</li>
            <li>토큰은 이용자가 서비스 화면에서 연결을 해제하면 즉시 삭제됩니다.</li>
          </UL>
          <H3>4) 자동 생성·수집 정보</H3>
          <UL>
            <li>서비스 이용 로그(오류·요청 처리 기록)</li>
            <li>인증 세션 쿠키(HttpOnly, OAuth state·return_url 등 로그인 흐름 유지 목적)</li>
            <li>브라우저 로컬 저장(localStorage): 최근 시청한 방송 목록, 테마 설정 등 이용자 기기에만 저장되는 편의 정보</li>
          </UL>
          <P>
            회사는 주민등록번호, 신용카드 정보, 계좌정보 등 결제 관련 민감정보를 <b>직접 수집·저장하지 않습니다.</b>
          </P>

          <H2 id="p3">제3조 (개인정보의 보유 및 이용기간)</H2>
          <Table>
            <thead>
              <tr className="bg-gray-100 dark:bg-[#1a1a1a] text-left">
                <th className="border border-black/10 dark:border-white/10 p-2">항목</th>
                <th className="border border-black/10 dark:border-white/10 p-2">보유 기간</th>
                <th className="border border-black/10 dark:border-white/10 p-2">근거</th>
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-gray-300">
              <tr>
                <td className="border border-black/10 dark:border-white/10 p-2">회원 계정 정보</td>
                <td className="border border-black/10 dark:border-white/10 p-2">회원 탈퇴 또는 서비스 종료 시 지체 없이 파기</td>
                <td className="border border-black/10 dark:border-white/10 p-2">이용자 동의</td>
              </tr>
              <tr>
                <td className="border border-black/10 dark:border-white/10 p-2">외부 플랫폼 OAuth 토큰</td>
                <td className="border border-black/10 dark:border-white/10 p-2">연결 해제 즉시 또는 회원 탈퇴 시</td>
                <td className="border border-black/10 dark:border-white/10 p-2">이용자 동의</td>
              </tr>
              <tr>
                <td className="border border-black/10 dark:border-white/10 p-2">계약·청약철회·소비자 불만·분쟁 처리 기록</td>
                <td className="border border-black/10 dark:border-white/10 p-2">각 3~5년</td>
                <td className="border border-black/10 dark:border-white/10 p-2">「전자상거래 등에서의 소비자보호에 관한 법률」</td>
              </tr>
              <tr>
                <td className="border border-black/10 dark:border-white/10 p-2">전자적 접속기록</td>
                <td className="border border-black/10 dark:border-white/10 p-2">3개월</td>
                <td className="border border-black/10 dark:border-white/10 p-2">「통신비밀보호법」</td>
              </tr>
            </tbody>
          </Table>

          <H2 id="p4">제4조 (개인정보의 제3자 제공)</H2>
          <P>
            회사는 원칙적으로 이용자의 개인정보를 제3자에게 제공하지 않습니다. 다만, 이용자의 사전 동의가
            있거나, 관련 법령에 따라 요구되는 경우에는 예외로 합니다. OAuth·API 통신을 통해 외부 플랫폼과
            주고받는 데이터는 이용자의 명시적 동의를 전제로 하며, 이를 「제3자 제공」과 구분하여 <b>제5조 국외
            이전</b> 및 <b>제6조 처리위탁</b>에서 별도로 안내합니다.
          </P>

          <H2 id="p5">제5조 (개인정보의 국외 이전)</H2>
          <P>
            서비스는 이용자가 외부 플랫폼 계정을 연결한 경우, 해당 플랫폼 운영사가 위치한 국외 서버로
            이용자의 인증정보 및 관련 요청 데이터가 전송됩니다.
          </P>
          <Table>
            <thead>
              <tr className="bg-gray-100 dark:bg-[#1a1a1a] text-left">
                <th className="border border-black/10 dark:border-white/10 p-2">이전받는 자</th>
                <th className="border border-black/10 dark:border-white/10 p-2">국가</th>
                <th className="border border-black/10 dark:border-white/10 p-2">항목</th>
                <th className="border border-black/10 dark:border-white/10 p-2">목적</th>
                <th className="border border-black/10 dark:border-white/10 p-2">보유·이용기간</th>
              </tr>
            </thead>
            <tbody className="text-gray-700 dark:text-gray-300">
              <tr>
                <td className="border border-black/10 dark:border-white/10 p-2">Meta Platforms, Inc.</td>
                <td className="border border-black/10 dark:border-white/10 p-2">미국 (아일랜드 등)</td>
                <td className="border border-black/10 dark:border-white/10 p-2">OAuth 인증 정보, Instagram 사용자 ID, 요청 스코프, Webhook 구독 상태</td>
                <td className="border border-black/10 dark:border-white/10 p-2">Instagram Business 계정 연결 · Reels 자동수집 · LIVE 댓글 수신</td>
                <td className="border border-black/10 dark:border-white/10 p-2">해당 플랫폼 정책에 따름</td>
              </tr>
              <tr>
                <td className="border border-black/10 dark:border-white/10 p-2">TikTok Pte. Ltd. (ByteDance 계열)</td>
                <td className="border border-black/10 dark:border-white/10 p-2">싱가포르 등</td>
                <td className="border border-black/10 dark:border-white/10 p-2">OAuth 인증 정보, open_id, 요청 스코프</td>
                <td className="border border-black/10 dark:border-white/10 p-2">TikTok 계정 연결·라이브 연동</td>
                <td className="border border-black/10 dark:border-white/10 p-2">해당 플랫폼 정책에 따름</td>
              </tr>
              <tr>
                <td className="border border-black/10 dark:border-white/10 p-2">Google LLC</td>
                <td className="border border-black/10 dark:border-white/10 p-2">미국</td>
                <td className="border border-black/10 dark:border-white/10 p-2">OAuth 인증 정보, 채널 ID</td>
                <td className="border border-black/10 dark:border-white/10 p-2">YouTube LIVE 연동</td>
                <td className="border border-black/10 dark:border-white/10 p-2">해당 플랫폼 정책에 따름</td>
              </tr>
              <tr>
                <td className="border border-black/10 dark:border-white/10 p-2">Kakao Corp.</td>
                <td className="border border-black/10 dark:border-white/10 p-2">대한민국</td>
                <td className="border border-black/10 dark:border-white/10 p-2">카카오 로그인 인증 정보</td>
                <td className="border border-black/10 dark:border-white/10 p-2">회원가입·로그인</td>
                <td className="border border-black/10 dark:border-white/10 p-2">해당 플랫폼 정책에 따름</td>
              </tr>
            </tbody>
          </Table>
          <P>
            이전 시점 및 방법: 이용자가 각 외부 플랫폼 계정을 최초로 연결하거나 인증을 수행하는 시점에
            HTTPS 를 통한 API 호출 방식으로 이전됩니다.
          </P>

          <H2 id="p6">제6조 (개인정보의 처리위탁)</H2>
          <P>
            회사는 현재 개인정보 처리를 별도 외부 사업자에게 위탁하지 않습니다. 향후 결제·정산·알림
            발송 등 처리위탁이 필요하게 되는 경우, 사전에 위탁받는 자·위탁업무 내용을 본 방침을 통해
            공개하고 필요한 경우 이용자의 동의를 받겠습니다.
          </P>

          <H2 id="p7">제7조 (이용자의 권리)</H2>
          <UL>
            <li>개인정보의 열람·정정·삭제·처리정지 요구</li>
            <li>동의 철회 및 회원 탈퇴</li>
            <li>외부 플랫폼 계정 연결의 해제(서비스 내 설정 또는 해당 플랫폼 계정 관리)</li>
          </UL>
          <P>
            위 권리는 서비스 내 설정 화면 또는 아래 <b>개인정보 보호책임자</b> 연락처로 요청하실 수 있으며,
            회사는 지체 없이 처리합니다. 외부 플랫폼(Instagram/Meta · YouTube · TikTok · Kakao) 연결 해제 및
            연동 데이터 삭제 절차의 상세 안내는{' '}
            <Link to="/privacy/data-deletion" className="underline hover:text-[#FF3B30]" data-testid="privacy-to-data-deletion">사용자 데이터 삭제 안내</Link>
            {' '}페이지를 참고해주세요.
          </P>

          <H2 id="p8">제8조 (개인정보의 파기)</H2>
          <P>
            개인정보 보유기간이 종료되거나 처리 목적이 달성된 개인정보는 지체 없이 파기합니다.
            전자적 파일 형태로 저장된 정보는 재생이 불가능한 방식으로 영구 삭제하며, 종이 문서는
            분쇄·소각의 방법으로 파기합니다.
          </P>

          <H2 id="p9">제9조 (안전성 확보 조치)</H2>
          <UL>
            <li>관리자·운영자 접근권한 최소화, 인증정보 서버측 관리</li>
            <li>OAuth 토큰 등 인증정보의 암호화 저장</li>
            <li>HTTPS(TLS) 를 통한 통신 구간 암호화</li>
            <li>인증 세션은 HttpOnly 쿠키 사용, 클라이언트에서 접근 불가</li>
            <li>운영 로그 관리 및 이상 접근 탐지</li>
          </UL>

          <H2 id="p10">제10조 (쿠키 및 유사 기술)</H2>
          <UL>
            <li>회사는 이용자의 로그인 흐름 유지 및 OAuth 상태 검증을 위해 HttpOnly 쿠키를 사용합니다.</li>
            <li>이용자의 편의(최근 시청 방송·테마 설정)를 위해 브라우저 <code>localStorage</code> 를 사용합니다. 이 데이터는 이용자 기기에만 저장되며 서버로 전송되지 않습니다.</li>
            <li>이용자는 브라우저 설정에서 쿠키 저장을 거부할 수 있으나, 이 경우 로그인 등 일부 기능 이용이 제한될 수 있습니다.</li>
          </UL>

          <H2 id="p11">제11조 (개인정보 보호책임자)</H2>
          <div className="my-3 rounded-lg border border-black/10 dark:border-white/10 p-4 text-sm">
            <div><b>개인정보 보호책임자</b>: 김현민</div>
            <div>소속: 엠지컴퍼니 (LIVEMOA 운영)</div>
            <div>이메일: <a href="mailto:k615745@naver.com" className="underline">k615745@naver.com</a></div>
            <div>전화: 010-6744-6344</div>
            <div className="mt-2 text-xs text-gray-500 dark:text-gray-400">
              ※ LIVEMOA 전용 이메일·대표전화 개설 후 연락처는 변경될 수 있으며, 변경 시 본 방침에 반영됩니다.
            </div>
          </div>

          <H2 id="p12">제12조 (권익침해 구제 방법)</H2>
          <P>
            개인정보 관련 분쟁 또는 상담이 필요한 경우 아래 기관에 문의하실 수 있습니다.
          </P>
          <UL>
            <li>개인정보 분쟁조정위원회 (kopico.go.kr / 1833-6972)</li>
            <li>개인정보침해 신고센터 (privacy.kisa.or.kr / 국번없이 118)</li>
            <li>대검찰청 사이버범죄수사단 (spo.go.kr / 국번없이 1301)</li>
            <li>경찰청 사이버안전지킴이 (cyberbureau.police.go.kr / 국번없이 182)</li>
          </UL>

          <H2 id="p13">제13조 (개정 이력)</H2>
          <UL>
            <li>2026년 2월 13일 — 최초 시행</li>
          </UL>

          <BusinessInfo />

          <p className="mt-10 text-xs text-gray-500 dark:text-gray-400">
            관련 문서: <Link to="/terms" className="underline hover:text-[#FF3B30]" data-testid="privacy-to-terms">이용약관</Link>
          </p>
        </article>
      </main>
    </div>
  );
}
